/**
 * Home Page - Stroyma
 * Cinematic hero with featured Faroese originals
 */

import { createFooter } from '../components/footer.js';
import { createNavigation } from '../components/navigation.js';
import { createVideoCard } from '../components/video-card.js';
import { api } from '../lib/api.js';
import { getCurrentLanguage, t } from '../lib/i18n.js';
import { isAuthenticated } from '../lib/keycloak.js';

export async function renderHome() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Navigation
  app.appendChild(await createNavigation());

  // Hero Section
  const hero = document.createElement('section');
  hero.className = 'hero';
  const heroImageUrl = api.resolveImageAsset(
    '/images/asset/c2505319-bc37-4566-9ff6-45f35ef0bb90?w=1280&fit=inside&q=80&fmt=webp',
  );
  hero.innerHTML = `
    <div class="hero-background">
      <img src="${heroImageUrl}" alt="Faroese landscape">
    </div>
    <div class="hero-content container">
      <h1 class="hero-title">${t('home_hero_title')}</h1>
      <p class="hero-subtitle">${t('home_hero_subtitle')}</p>
      <div class="hero-cta">
        <button class="btn btn-primary" id="scroll-to-documentary">${t('home_hero_cta')}</button>
        <a href="/about" class="btn btn-secondary" data-link>${t('home_hero_learn')}</a>
      </div>
    </div>
  `;
  app.appendChild(hero);

  // Add scroll handler for "Byrja at hyggja" button
  setTimeout(() => {
    const scrollButton = document.getElementById('scroll-to-documentary');
    if (scrollButton) {
      scrollButton.addEventListener('click', () => {
        const documentarySection = document.getElementById('documentary-section');
        if (documentarySection) {
          documentarySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }, 0);

  // Continue Watching Section (only for authenticated users)
  if (isAuthenticated()) {
    const continueWatchingSection = document.createElement('section');
    continueWatchingSection.className = 'section';
    continueWatchingSection.id = 'continue-watching-section';
    continueWatchingSection.innerHTML = `
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 8px;">
              <path d="M8 5v14l11-7z"/>
            </svg>
            ${t('continue_watching_title') || 'Continue Watching'}
          </h2>
          <p class="section-subtitle">${t('continue_watching_subtitle') || 'Pick up where you left off'}</p>
        </div>
        <div id="continue-watching-grid" class="video-grid">
          <div class="loading-container">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    `;
    app.appendChild(continueWatchingSection);

    // Load continue watching videos
    loadContinueWatching();
  }

  // Documentary Section
  const documentarySection = document.createElement('section');
  documentarySection.className = 'section';
  documentarySection.id = 'documentary-section';
  documentarySection.innerHTML = `
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">${t('category_documentary')}</h2>
      </div>
      <div id="documentary-grid" class="video-grid">
        <div class="loading-container">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  `;
  app.appendChild(documentarySection);

  // Short Feature Section
  const shortSection = document.createElement('section');
  shortSection.className = 'section';
  shortSection.innerHTML = `
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">${t('category_short')}</h2>
      </div>
      <div id="short-grid" class="video-grid">
        <div class="loading-container">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  `;
  app.appendChild(shortSection);

  // Feature Section
  const featureSection = document.createElement('section');
  featureSection.className = 'section';
  featureSection.innerHTML = `
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">${t('category_feature')}</h2>
      </div>
      <div id="feature-grid" class="video-grid">
        <div class="loading-container">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  `;
  app.appendChild(featureSection);

  // Load videos
  try {
    console.log('[Home] Fetching videos...');
    const data = await api.getVideos();
    console.log('[Home] Received data:', data);
    const videos = data.videos || [];
    console.log('[Home] Videos array:', videos);
    console.log('[Home] Video count:', videos.length);

    // Filter by category
    const documentaries = videos.filter(
      (v) => v.category === 'documentary' || v.category === 'documentary_short' || v.category === 'documentary_long',
    );
    const shorts = videos.filter((v) => v.category === 'short');
    const features = videos.filter((v) => v.category === 'feature');
    console.log('[Home] Filtered counts:', {
      documentaries: documentaries.length,
      shorts: shorts.length,
      features: features.length,
    });

    // Render documentaries
    const documentaryGrid = document.getElementById('documentary-grid');
    documentaryGrid.innerHTML = '';

    if (documentaries.length > 0) {
      documentaries.forEach((video) => {
        documentaryGrid.appendChild(createVideoCard(video));
      });
    } else {
      documentaryGrid.innerHTML = `<p class="text-center" style="color: var(--color-cloud-grey); grid-column: 1/-1;">${t('no_videos')}</p>`;
    }

    // Render shorts
    const shortGrid = document.getElementById('short-grid');
    shortGrid.innerHTML = '';

    if (shorts.length > 0) {
      shorts.forEach((video) => {
        shortGrid.appendChild(createVideoCard(video));
      });
    } else {
      shortGrid.innerHTML = `<p class="text-center" style="color: var(--color-cloud-grey); grid-column: 1/-1;">${t('no_videos')}</p>`;
    }

    // Render features
    const featureGrid = document.getElementById('feature-grid');
    featureGrid.innerHTML = '';

    if (features.length > 0) {
      features.forEach((video) => {
        featureGrid.appendChild(createVideoCard(video));
      });
    } else {
      featureGrid.innerHTML = `<p class="text-center" style="color: var(--color-cloud-grey); grid-column: 1/-1;">${t('no_videos')}</p>`;
    }
  } catch (error) {
    console.error('Failed to load videos:', error);
    document.getElementById('documentary-grid').innerHTML =
      '<p class="text-center" style="color: var(--color-cloud-grey); grid-column: 1/-1;">Failed to load videos. Please try again later.</p>';
  }

  // Footer
  app.appendChild(createFooter());
}

// Load continue watching videos
async function loadContinueWatching() {
  const grid = document.getElementById('continue-watching-grid');
  if (!grid) return;

  try {
    const data = await api.getContinueWatching();
    const videos = data.videos || [];

    if (videos.length === 0) {
      // Hide the entire section if no videos to continue watching
      const section = document.getElementById('continue-watching-section');
      if (section) {
        section.style.display = 'none';
      }
      return;
    }

    grid.innerHTML = '';
    const currentLang = getCurrentLanguage();

    videos.forEach((video) => {
      const progressPercent = video.watchProgress?.percentWatched || 0;
      const title =
        currentLang === 'fo'
          ? video.originalTitle || video.internationalTitle
          : video.internationalTitle || video.originalTitle;

      // Create a custom video card with progress bar
      const card = document.createElement('div');
      card.className = 'video-card';
      card.innerHTML = `
        <a href="/watch/${video.videoId}" class="video-card-link" data-link>
          <div class="video-card-thumbnail">
            ${
              video.thumbnail
                ? `<img src="${video.thumbnail}" alt="${title}" loading="lazy">`
                : `
              <div class="video-card-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            `
            }
            <div class="video-card-progress">
              <div class="video-card-progress-bar" style="width: ${Math.min(progressPercent, 100)}%"></div>
            </div>
            <div class="video-card-overlay">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="play-icon">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
          <div class="video-card-content">
            <h3 class="video-card-title">${title}</h3>
            <p class="video-card-meta">
              ${Math.round(progressPercent)}% ${t('continue_watching_watched') || 'watched'}
            </p>
          </div>
        </a>
      `;
      grid.appendChild(card);
    });
  } catch (error) {
    console.error('Failed to load continue watching:', error);
    // Hide section on error
    const section = document.getElementById('continue-watching-section');
    if (section) {
      section.style.display = 'none';
    }
  }
}
